import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Select, Button, Radio, DatePicker, Flex } from 'antd';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';

import AutomaticDrawer from 'components/Drawers/AutomaticDrawer';
import useQueryParams from 'hooks/useQueryParams';
import useWorkspace from 'store/redux/hooks/useWorkspace';
import {
  useCreateCustomUrlCostMutation,
  useUpdateCustomUrlCostMutation,
  useDeleteUrlCustomCostMutation,
} from 'modules/UrlModule/api/custom-url-costs.api';
import { snackbar } from 'components/notifications/notifications';
import ninjaApi from 'store/redux/apis';
import Currency from 'components/Currency';
import UrlCostPreview from './components/UrlCostPreview';
import formikFieldToAntProps from '@lib/formik/formikFieldToAntProps';
import Icon from '@mdi/react';
import { mdiReload } from '@mdi/js';
import Confirmation from 'components/Dialogs/Confirmation';

const CustomUrlCostDrawer = (props) => {
  const { searchParams, removeParams } = useQueryParams();
  const { workspace } = useWorkspace();
  const { url_id } = searchParams;

  const [getCustomCost] = ninjaApi.useLazyFindCustomCostQuery();
  const [createCost, { isLoading }] = useCreateCustomUrlCostMutation();
  const [deleteUrlCustomCost, { isLoading: deleteloading }] = useDeleteUrlCustomCostMutation();
  const [updateCost] = useUpdateCustomUrlCostMutation();

  const [data, setData] = useState();
  const isUpdate = Boolean(data?.data);

  useEffect(() => {
    if (url_id) {
      getCustomCost({ url_id })
        .unwrap()
        .then((data) => setData(data))
        .catch(() => {
          setData(null);
          setPreviewData(null);
        });
    }
  }, [url_id]);

  const handleSubmitOrPreview = useCallback(
    async (formValues, isPreview = false) => {
      const mutation = isUpdate ? updateCost : createCost;

      const payload = {
        ...formValues,
        ...(formValues.cost_type === 'recurring' && { period_type: formValues.period_type }),
        ...((formValues.cost_type === 'fixed' ||
          formValues.recurring_range_type !== 'infinite') && {
          date_from: formValues.date_from,
          date_to: formValues.date_to,
        }),
        ...(isPreview && {
          preview: true,
          id: isUpdate ? data?.data?.id : undefined,
        }),
      };

      try {
        if (isPreview) {
          const result = await mutation(payload).unwrap();
          setPreviewData(result);
        } else {
          await mutation(payload).unwrap();
          snackbar.success(isUpdate ? 'URL cost updated' : 'URL cost created');
          removeParams(['create_custom_url_cost', 'url_id']);
        }
      } catch (err) {}
    },
    [isUpdate, createCost, updateCost, data?.data?.id]
  );

  const handleDeleteCustomCost = () => {
    deleteUrlCustomCost({ id: data?.data?.id })
      .unwrap()
      .then(() => {
        setData(null);
        setPreviewData(null);
      });
    setPreviewData(null);
    form.resetForm();
  };

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.data?.name || '',
      url_id: url_id,
      cost_type: data?.data?.cost_type || 'fixed',
      cost_value: Number(data?.data?.cost_value) || '',
      currency: data?.data?.currency || workspace?.currency || 'USD',
      period_type: data?.data?.period_type || null,
      recurring_range_type: data?.data?.recurring_range_type || 'infinite',
      date_from: data?.data?.date_from || dayjs().format('YYYY-MM-DD'),
      date_to: data?.data?.date_to || dayjs().format('YYYY-MM-DD'),
      id: isUpdate ? data?.data?.id : undefined,
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: (values) => handleSubmitOrPreview(values),
  });

  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    if (form.values.cost_value || form.values.period_type) {
      const handler = setTimeout(() => {
        handleSubmitOrPreview(form.values, true);
      }, 1000);
      return () => clearTimeout(handler);
    }
  }, [
    form.values.cost_value,
    form.values.period_type,
    form.values.date_from,
    form.values.cost_type,
  ]);

  return (
    <AutomaticDrawer
      title={isUpdate ? 'Update URL Cost' : 'Create URL Cost'}
      size='small'
      {...props}
    >
      <Form layout='vertical' onFinish={form.handleSubmit}>
        <Flex justify='space-between' align='center'>
          <Form.Item>
            <Radio.Group
              value={form.values.cost_type}
              onChange={(e) => form.setFieldValue('cost_type', e.target.value)}
            >
              <Radio value='fixed'>Fixed</Radio>
              <Radio value='recurring'>Recurring</Radio>
            </Radio.Group>
          </Form.Item>
          {(data?.data || previewData) && (
            <Confirmation
              className=''
              onConfirm={handleDeleteCustomCost}
              description='Are you sure?'
            >
              <Button
                isLoading={deleteloading}
                icon={<Icon path={mdiReload} size={0.6} />}
                size='midlle'
              >
                Reset
              </Button>
            </Confirmation>
          )}
        </Flex>

        <Form.Item label='Name'>
          <Input
            name='name'
            placeholder='Enter name'
            value={form.values.name}
            onChange={(e) => form.setFieldValue('name', e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label='Cost Value'
          {...formikFieldToAntProps(form.errors, form.touched, 'cost_value')}
        >
          <Input
            name='cost_value'
            placeholder='value'
            type='number'
            value={form.values.cost_value}
            onChange={(e) => form.setFieldValue('cost_value', Number(e.target.value))}
            addonAfter={
              <Currency
                className='w-24'
                value={form.values.currency}
                onChange={(val) => form.setFieldValue('currency', val)}
              />
            }
          />
        </Form.Item>

        {form.values.cost_type === 'recurring' && (
          <>
            <Form.Item
              label='Period Type'
              {...formikFieldToAntProps(form.errors, form.touched, 'period_type')}
            >
              <Select
                placeholder='Select period'
                value={form.values.period_type}
                onChange={(val) => form.setFieldValue('period_type', val)}
                options={['daily', 'weekly', 'monthly', 'yearly'].map((v) => ({
                  value: v,
                  label: v[0].toUpperCase() + v.slice(1),
                }))}
              />
            </Form.Item>

            <Form.Item>
              <Radio.Group
                value={form.values.recurring_range_type}
                onChange={(e) => form.setFieldValue('recurring_range_type', e.target.value)}
              >
                <Radio value='infinite'>Infinite</Radio>
                <Radio value='custom'>Custom Date</Radio>
              </Radio.Group>
            </Form.Item>
          </>
        )}

        <Form.Item label='Date Range'>
          <DatePicker.RangePicker
            value={[dayjs(form.values.date_from), dayjs(form.values.date_to)]}
            onChange={(_, [from, to]) => {
              form.setFieldValue('date_from', from);
              form.setFieldValue('date_to', to);
            }}
            disabled={
              form.values.cost_type === 'recurring' && form.values.recurring_range_type !== 'custom'
            }
          />
        </Form.Item>

        <UrlCostPreview isLoading={isLoading} previewData={previewData} />

        <div className='flex-center mt-4'>
          <Button size='large' type='primary' htmlType='submit'>
            {isUpdate ? 'Update' : 'Create'}
          </Button>
        </div>
      </Form>
    </AutomaticDrawer>
  );
};

CustomUrlCostDrawer.requiredParams = {
  create_custom_url_cost: true,
  url_id: (p) => parseInt(p) > 0,
};

export default CustomUrlCostDrawer;

const validationSchema = yup.object({
  cost_value: yup.string().required('Cost value is required'),
  period_type: yup.mixed().when('cost_type', {
    is: 'recurring',
    then: (s) => s.required('Period type is required'),
  }),
});
